import {comparePoolResourceNames, maxQuantityForBulkAssignGap} from 'src/equipment/stock-equipment'

import type {RentalAsset} from '../assets/rental-assets/schemas'
import type {ThirdPartyRentalAssetOffering} from '../assets/rental-assets/schemas'
import type {Worker} from '../assets/workers/schemas'
import type {JobStockEquipmentGroup} from './stock-equipment/schemas'
import type {AssignmentInput, JobDetail, JobDetailAssignment, RequirementInput} from './types'

export type FulfillmentGroupKind = 'worker' | 'rental_asset'

export type FulfillmentStatus = 'met' | 'under' | 'over'

export type PoolResource = {
    id: string
    name: string
    kind: FulfillmentGroupKind
    isPlaceholder?: boolean
    unavailableReason?: string
}

export type FulfillmentAssignment = {
    id?: string
    workerId: string | null
    rentalAssetId: string | null
    workerName?: string | null
    rentalAssetName?: string | null
    rentalAssetTypeId?: string | null
    isThirdParty?: boolean
}

export type FulfillmentRequirement = {
    id?: string
    requirementType: 'worker' | 'rental_asset'
    quantity: number
    workerTypeId?: string | null
    workerTypeName?: string | null
    rentalAssetTypeId: string | null
    rentalAssetTypeName?: string | null
    billingRate: string | null
    billingType: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'flat' | 'per_load' | null
}

export type FulfillmentGroup = {
    key: string
    kind: FulfillmentGroupKind
    label: string
    workerTypeId: string | null
    rentalAssetTypeId: string | null
    requirementId: string | null
    needed: number
    assigned: FulfillmentAssignment[]
    stockEquipmentGroups?: JobStockEquipmentGroup[]
    status: FulfillmentStatus
}

const LEGACY_WORKER_GROUP_KEY = 'worker'

function fulfillmentStatus(needed: number, assignedCount: number): FulfillmentStatus {
    if (assignedCount < needed) {
        return 'under'
    }
    if (assignedCount > needed) {
        return 'over'
    }
    return 'met'
}

function enrichAssignment(
    assignment: FulfillmentAssignment,
    rentalAssetsById?: Map<string, RentalAsset>,
    workersById?: Map<string, Worker>,
): FulfillmentAssignment {
    let enriched = assignment

    if (assignment.workerId != null && assignment.workerName == null) {
        const worker = workersById?.get(assignment.workerId)
        if (worker) {
            enriched = {...enriched, workerName: worker.name}
        }
    }

    if (assignment.rentalAssetId != null && assignment.rentalAssetTypeId == null) {
        const rentalAsset = rentalAssetsById?.get(assignment.rentalAssetId)
        if (rentalAsset) {
            enriched = {
                ...enriched,
                rentalAssetName: enriched.rentalAssetName ?? rentalAsset.name,
                rentalAssetTypeId: rentalAsset.rentalAssetTypeId,
            }
        }
    } else if (
        assignment.rentalAssetId != null &&
        assignment.rentalAssetName == null &&
        rentalAssetsById
    ) {
        const rentalAsset = rentalAssetsById.get(assignment.rentalAssetId)
        if (rentalAsset) {
            enriched = {...enriched, rentalAssetName: rentalAsset.name}
        }
    }

    return enriched
}

function enrichAssignments(
    assignments: FulfillmentAssignment[],
    rentalAssetsById?: Map<string, RentalAsset>,
    workersById?: Map<string, Worker>,
): FulfillmentAssignment[] {
    return assignments.map((assignment) =>
        enrichAssignment(assignment, rentalAssetsById, workersById),
    )
}

export function buildFulfillmentGroups(
    requirements: FulfillmentRequirement[],
    assignments: FulfillmentAssignment[],
    rentalAssetsById?: Map<string, RentalAsset>,
    workersById?: Map<string, Worker>,
    stockEquipmentGroups: JobStockEquipmentGroup[] = [],
): FulfillmentGroup[] {
    const enrichedAssignments = enrichAssignments(assignments, rentalAssetsById, workersById)
    const groups: FulfillmentGroup[] = []

    const hasTypedWorkerRequirements = requirements.some(
        (requirement) => requirement.requirementType === 'worker' && requirement.workerTypeId,
    )

    if (!hasTypedWorkerRequirements) {
        const workerRequirement = requirements.find(
            (requirement) => requirement.requirementType === 'worker',
        )
        const workerNeeded = workerRequirement?.quantity ?? 0
        const workerAssigned = enrichedAssignments.filter(
            (assignment) => assignment.workerId != null,
        )

        if (workerNeeded > 0 || workerAssigned.length > 0) {
            groups.push({
                key: LEGACY_WORKER_GROUP_KEY,
                kind: 'worker',
                label: 'Workers',
                workerTypeId: null,
                rentalAssetTypeId: null,
                requirementId: workerRequirement?.id ?? null,
                needed: workerNeeded,
                assigned: workerAssigned,
                status: fulfillmentStatus(workerNeeded, workerAssigned.length),
            })
        }
    } else {
        const workerTypeLabels = new Map<string, string>()
        const workerRequirementIdsByType = new Map<string, string>()

        for (const requirement of requirements) {
            if (requirement.requirementType === 'worker' && requirement.workerTypeId) {
                workerTypeLabels.set(
                    requirement.workerTypeId,
                    requirement.workerTypeName ?? 'Worker',
                )
                if (requirement.id) {
                    workerRequirementIdsByType.set(requirement.workerTypeId, requirement.id)
                }
            }
        }

        for (const assignment of enrichedAssignments) {
            if (assignment.workerId != null && workersById) {
                const workerTypeId = workersById.get(assignment.workerId)?.workerTypeId
                if (workerTypeId && !workerTypeLabels.has(workerTypeId)) {
                    workerTypeLabels.set(workerTypeId, 'Worker')
                }
            }
        }

        for (const [typeId, typeName] of workerTypeLabels) {
            const needed =
                requirements.find(
                    (requirement) =>
                        requirement.requirementType === 'worker' &&
                        requirement.workerTypeId === typeId,
                )?.quantity ?? 0
            const assigned = enrichedAssignments.filter((assignment) => {
                if (assignment.workerId == null) {
                    return false
                }

                return workersById?.get(assignment.workerId)?.workerTypeId === typeId
            })

            groups.push({
                key: typeId,
                kind: 'worker',
                label: typeName,
                workerTypeId: typeId,
                rentalAssetTypeId: null,
                requirementId: workerRequirementIdsByType.get(typeId) ?? null,
                needed,
                assigned,
                status: fulfillmentStatus(needed, assigned.length),
            })
        }
    }

    const typeLabels = new Map<string, string>()
    const requirementIdsByType = new Map<string, string>()
    for (const requirement of requirements) {
        if (requirement.requirementType === 'rental_asset' && requirement.rentalAssetTypeId) {
            typeLabels.set(
                requirement.rentalAssetTypeId,
                requirement.rentalAssetTypeName ?? 'rental asset',
            )
            if (requirement.id) {
                requirementIdsByType.set(requirement.rentalAssetTypeId, requirement.id)
            }
        }
    }

    for (const assignment of enrichedAssignments) {
        if (assignment.rentalAssetId != null && assignment.rentalAssetTypeId) {
            if (!typeLabels.has(assignment.rentalAssetTypeId)) {
                typeLabels.set(assignment.rentalAssetTypeId, 'rental asset')
            }
        }
    }

    for (const group of stockEquipmentGroups) {
        typeLabels.set(group.rentalAssetTypeId, group.rentalAssetTypeName)
    }
    for (const [typeId, typeName] of typeLabels) {
        const needed =
            requirements.find(
                (requirement) =>
                    requirement.requirementType === 'rental_asset' &&
                    requirement.rentalAssetTypeId === typeId,
            )?.quantity ?? 0
        const assigned = enrichedAssignments.filter(
            (assignment) =>
                assignment.rentalAssetId != null && assignment.rentalAssetTypeId === typeId,
        )

        const stockGroups = stockEquipmentGroups.filter(
            (group) => group.rentalAssetTypeId === typeId,
        )
        groups.push({
            key: typeId,
            kind: 'rental_asset',
            label: typeName,
            workerTypeId: null,
            rentalAssetTypeId: typeId,
            requirementId: requirementIdsByType.get(typeId) ?? null,
            needed,
            assigned,
            stockEquipmentGroups: stockGroups,
            status: fulfillmentStatus(
                needed,
                assigned.length +
                    stockGroups.reduce((sum, group) => sum + group.assignedQuantity, 0),
            ),
        })
    }

    return groups
}

export function getAssignedResourceIds(assignments: FulfillmentAssignment[]): Set<string> {
    const ids = new Set<string>()
    for (const assignment of assignments) {
        if (assignment.workerId != null) {
            ids.add(assignment.workerId)
        }
        if (assignment.rentalAssetId != null) {
            ids.add(assignment.rentalAssetId)
        }
    }
    return ids
}

export function thirdPartyOfferingLabel(offering: ThirdPartyRentalAssetOffering): string {
    return `${offering.companyName} — ${offering.rentalAssetTypeName}`
}

/** Units with an entry in `unavailableReasons` sort after the available units. */
export function getAvailablePool(
    group: FulfillmentGroup,
    assignments: FulfillmentAssignment[],
    workers: Worker[],
    rentalAssets: RentalAsset[],
    unavailableReasons: Map<string, string> = new Map(),
): PoolResource[] {
    const assignedIds = getAssignedResourceIds(assignments)

    if (group.kind === 'worker') {
        return workers
            .filter((worker) => {
                if (assignedIds.has(worker.id)) {
                    return false
                }

                if (group.workerTypeId == null) {
                    return true
                }

                return worker.workerTypeId === group.workerTypeId
            })
            .map((worker) => ({
                id: worker.id,
                name: worker.name,
                kind: 'worker' as const,
                ...(worker.isPlaceholder ? {isPlaceholder: true} : {}),
            }))
            .sort(
                (left, right) => Number(left.isPlaceholder ?? 0) - Number(right.isPlaceholder ?? 0),
            )
    }

    return rentalAssets
        .filter(
            (rentalAsset) =>
                rentalAsset.rentalAssetTypeId === group.rentalAssetTypeId &&
                !assignedIds.has(rentalAsset.id),
        )
        .map((rentalAsset) => {
            const unavailableReason = unavailableReasons.get(rentalAsset.id)
            return {
                id: rentalAsset.id,
                name: rentalAsset.name ?? rentalAsset.rentalAssetTypeName,
                kind: 'rental_asset' as const,
                ...(unavailableReason ? {unavailableReason} : {}),
            }
        })
        .sort(
            (left, right) =>
                Number(left.unavailableReason != null) - Number(right.unavailableReason != null) ||
                comparePoolResourceNames(left.name, right.name),
        )
}

export function maxBulkAssignQuantityForGroup(group: FulfillmentGroup, poolSize: number): number {
    return maxQuantityForBulkAssignGap(group.needed, fulfillmentAssignedQuantity(group), poolSize)
}

export function assignmentsToInputs(assignments: JobDetail['assignments']): AssignmentInput[] {
    return assignments.map((assignment) => ({
        id: assignment.id,
        workerId: assignment.workerId,
        rentalAssetId: assignment.rentalAssetId,
    }))
}

export function requirementsToInputs(requirements: JobDetail['requirements']): RequirementInput[] {
    return requirements.map((requirement) => ({
        id: requirement.id,
        requirementType: requirement.requirementType,
        quantity: requirement.quantity,
        workerTypeId: requirement.workerTypeId ?? undefined,
        rentalAssetTypeId: requirement.rentalAssetTypeId,
        billingRate: requirement.billingRate,
        billingType: requirement.billingType,
    }))
}

export function addAssignment(
    assignments: AssignmentInput[],
    group: FulfillmentGroup,
    resourceId: string,
    source: 'first_party' | 'third_party_offering' = 'first_party',
    quantity = 1,
): AssignmentInput[] {
    if (group.kind === 'worker') {
        return [...assignments, {workerId: resourceId, rentalAssetId: null}]
    }

    if (source === 'third_party_offering') {
        return [
            ...assignments,
            {
                workerId: null,
                rentalAssetId: null,
                thirdPartyOfferingId: resourceId,
                ...(quantity > 1 ? {quantity} : {}),
            },
        ]
    }

    return [...assignments, {workerId: null, rentalAssetId: resourceId}]
}

export function removeAssignment(
    assignments: AssignmentInput[],
    resourceId: string,
): AssignmentInput[] {
    return assignments.filter(
        (assignment) =>
            assignment.workerId !== resourceId && assignment.rentalAssetId !== resourceId,
    )
}

export function fulfillmentGroupsFromJobDetail(
    job: JobDetail,
    workers: Worker[] = [],
): FulfillmentGroup[] {
    const requirements: FulfillmentRequirement[] = job.requirements.map((requirement) => ({
        id: requirement.id,
        requirementType: requirement.requirementType,
        quantity: requirement.quantity,
        workerTypeId: requirement.workerTypeId ?? null,
        workerTypeName:
            requirement.requirementType === 'worker' ? requirement.rentalAssetTypeName : null,
        rentalAssetTypeId: requirement.rentalAssetTypeId,
        rentalAssetTypeName: requirement.rentalAssetTypeName,
        billingRate: requirement.billingRate,
        billingType: requirement.billingType,
    }))
    const assignments: FulfillmentAssignment[] = job.assignments.map(
        (assignment: JobDetailAssignment) => ({
            id: assignment.id,
            workerId: assignment.workerId,
            rentalAssetId: assignment.rentalAssetId,
            workerName: assignment.workerName,
            rentalAssetName: assignment.rentalAssetName,
            rentalAssetTypeId: assignment.rentalAssetTypeId,
            ...(assignment.isThirdParty ? {isThirdParty: true} : {}),
        }),
    )
    const workersById = new Map(workers.map((worker) => [worker.id, worker]))

    return buildFulfillmentGroups(
        requirements,
        assignments,
        undefined,
        workersById,
        job.stockEquipmentGroups,
    )
}

export function emptySlotCount(group: FulfillmentGroup): number {
    if (group.needed === 0) {
        return 0
    }
    return Math.max(0, group.needed - fulfillmentAssignedQuantity(group))
}

/** Third-party job assignments also have rental asset ids, but only fleet units support the peek. */
export function isFirstPartyFleetRentalAssetId(
    assetId: string,
    firstPartyRentalAssetIds: ReadonlySet<string>,
): boolean {
    return firstPartyRentalAssetIds.has(assetId)
}

export function fulfillmentAssignedQuantity(group: FulfillmentGroup): number {
    return (
        group.assigned.length +
        (group.stockEquipmentGroups ?? []).reduce((sum, stock) => sum + stock.assignedQuantity, 0)
    )
}
